/**
 * Radio Group Component
 *
 * A reusable component to present a list of radio buttons with staged selection
 * and explicit Apply/Cancel actions. Uses the existing RadioGroup primitives and
 * aligns with the UI Core design system (labels, descriptions, buttons, spacing).
 */

import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { Circle } from "lucide-react"
import { cn } from "@strivelabs/ui-core"
import { Button } from "@strivelabs/ui-core/v2"

export interface RadioOption {
  /** Unique value for the option */
  value: string
  /** Human-readable label */
  label: string
  /** Optional helper or description text */
  description?: string
  /** Disable a specific option */
  disabled?: boolean
}

export interface RadioGroupProps {
  /** Options to render */
  options: RadioOption[]
  /** Controlled selected value from the parent */
  selectedValue: string | null
  /** Called when user clicks Apply; receives the staged selection */
  onApply: (value: string | null) => void
  /** Optional callback when user clicks Cancel */
  onCancel?: () => void
  /** Optional callback fired on every staged change (before Apply) */
  onTempChange?: (value: string | null) => void

  /** Label rendered above the group */
  label?: string
  /** Description text under the label */
  description?: string
  /** Disable the whole group */
  disabled?: boolean

  /** Text for action buttons */
  applyButtonText?: string
  cancelButtonText?: string

  /** Custom classes */
  className?: string
  listClassName?: string
  itemClassName?: string
  actionsClassName?: string
  /** Optional name/id for accessibility */
  name?: string
  id?: string
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      options,
      selectedValue,
      onApply,
      onCancel,
      onTempChange,
      label,
      description,
      disabled = false,
      applyButtonText = "Apply",
      cancelButtonText = "Cancel",
      className,
      listClassName,
      itemClassName,
      actionsClassName,
      name,
      id,
      ...props
    },
    ref
  ) => {
    const [staged, setStaged] = React.useState<string | null>(selectedValue)

    // Sync staged state when external selection changes
    React.useEffect(() => {
      setStaged(selectedValue)
    }, [selectedValue])

    const handleValueChange = React.useCallback(
      (value: string) => {
        setStaged(value)
        onTempChange?.(value)
      },
      [onTempChange]
    )

    const handleApply = React.useCallback(() => {
      onApply(staged)
    }, [onApply, staged])

    const handleCancel = React.useCallback(() => {
      // Just notify cancel without resetting state - parent decides what to do
      onCancel?.()
    }, [onCancel])

    return (
      <div
        ref={ref}
        className={cn(
          "bg-ai-element border-half border-custom-border rounded-lg p-5",
          className
        )}
        {...props}
      >
        {label && (
          <p id={id} className="mb-5 text-base font-medium text-black">
            {label}
          </p>
        )}
        {description && (
          <p className="mb-5 text-sm text-muted-foreground">{description}</p>
        )}

        <RadioGroupPrimitive.Root
          value={staged || ""}
          onValueChange={handleValueChange}
          disabled={disabled}
          name={name}
          className={cn("flex flex-col gap-2", listClassName)}
        >
          {options.map((opt, idx) => {
            const optionId = `${id || name || "radio-group"}-${idx}`

            return (
              <div
                key={opt.value}
                className={cn(
                  "flex items-center gap-3",
                  itemClassName
                )}
                onClick={() => {
                  if (!disabled && !opt.disabled) {
                    handleValueChange(opt.value)
                  }
                }}
              >
                <RadioGroupPrimitive.Item
                  id={optionId}
                  value={opt.value}
                  disabled={disabled || opt.disabled}
                  className={cn(
                    "aspect-square size-4 rounded-full border border-custom-border text-custom-text-primary",
                    "ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    "disabled:cursor-not-allowed disabled:opacity-50"
                  )}
                >
                  <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
                    <Circle className="h-2.5 w-2.5 fill-current text-current" />
                  </RadioGroupPrimitive.Indicator>
                </RadioGroupPrimitive.Item>
                <label
                  htmlFor={optionId}
                  className={cn(
                    "cursor-pointer select-none flex-1",
                    (disabled || opt.disabled) && "cursor-not-allowed"
                  )}
                >
                  <div className="text-sm font-normal leading-5 text-custom-text-primary">
                    {opt.label}
                  </div>
                  {opt.description && (
                    <div className="text-sm text-muted-foreground">
                      {opt.description}
                    </div>
                  )}
                </label>
              </div>
            )
          })}
        </RadioGroupPrimitive.Root>

        <div className={cn("flex items-center justify-end gap-2 mt-5", actionsClassName)}>
          <Button
            variant="secondary"
            size="xs"
            onClick={handleCancel}
            disabled={disabled}
          >
            {cancelButtonText}
          </Button>
          <Button
            size="xs"
            onClick={handleApply}
            disabled={disabled}
          >
            {applyButtonText}
          </Button>
        </div>
      </div>
    )
  }
)

RadioGroup.displayName = "RadioGroup"

export { RadioGroup }
